import type { Book, BudgetRule } from "@/schemas";

import { categoryLabel, sanitizeKey } from "./budget-helpers";

export interface BudgetPlanNode {
  id: string;
  label: string;
  type: string;
  name: string;
  amount: number;
}

export interface BudgetPlanLink {
  source: number;
  target: number;
  value: number;
}

export interface BudgetPlan {
  totalIncome: number;
  totalExpenses: number;
  totalInvestments: number;
  remainingToAllocate: number;
  nodes: BudgetPlanNode[];
  links: BudgetPlanLink[];
}

export function buildBudgetPlan(rules: BudgetRule[], books: Book[]): BudgetPlan {
  const enabledRules = rules.filter((rule) => rule.enabled);
  const nodes: BudgetPlanNode[] = [];
  const rawLinks: Array<{ source: string; target: string; value: number }> = [];
  const bookById = new Map(books.map((book) => [book.book_id, book]));
  const orderedCategoryIds: string[] = [];

  let totalIncome = 0;
  let totalExpenses = 0;
  let totalInvestments = 0;

  const upsertNode = (
    id: string,
    label: string,
    type: string,
    amount: number,
  ) => {
    const existing = nodes.find((node) => node.id === id);
    if (existing) {
      existing.amount += amount;
      return;
    }
    nodes.push({ id, label, type, name: label, amount });
  };

  upsertNode("budget", "Budget", "budget", 0);

  enabledRules.forEach((rule) => {
    const itemNodeId =
      rule.kind === "investment" && rule.linked_book_id
        ? `book:${rule.linked_book_id}`
        : `${rule.kind}:${sanitizeKey(rule.id)}:${sanitizeKey(rule.label)}`;
    const itemNodeLabel =
      rule.kind === "investment" && rule.linked_book_id
        ? bookById.get(rule.linked_book_id)?.name ||
          rule.label ||
          categoryLabel(rule.category)
        : rule.label || categoryLabel(rule.category);

    if (rule.kind === "income") {
      totalIncome += rule.amount;
      upsertNode(itemNodeId, itemNodeLabel, "income", rule.amount);
      rawLinks.push({
        source: itemNodeId,
        target: "budget",
        value: rule.amount,
      });
      return;
    }

    const groupLabel = categoryLabel(rule.category);
    const groupNodeId =
      rule.kind === "investment"
        ? `group:investment:${sanitizeKey(groupLabel)}`
        : `group:expense:${sanitizeKey(groupLabel)}`;

    if (!orderedCategoryIds.includes(groupNodeId)) {
      orderedCategoryIds.push(groupNodeId);
    }

    if (rule.kind === "expense") {
      totalExpenses += rule.amount;
      upsertNode(groupNodeId, groupLabel, "expense_group", rule.amount);
      upsertNode(itemNodeId, itemNodeLabel, "expense", rule.amount);
      rawLinks.push({
        source: "budget",
        target: groupNodeId,
        value: rule.amount,
      });
      rawLinks.push({
        source: groupNodeId,
        target: itemNodeId,
        value: rule.amount,
      });
      return;
    }

    totalExpenses += rule.amount;
    totalInvestments += rule.amount;
    upsertNode(groupNodeId, groupLabel, "investment_group", rule.amount);
    upsertNode(itemNodeId, itemNodeLabel, "book", rule.amount);
    rawLinks.push({
      source: "budget",
      target: groupNodeId,
      value: rule.amount,
    });
    rawLinks.push({
      source: groupNodeId,
      target: itemNodeId,
      value: rule.amount,
    });
  });

  const budgetNode = nodes.find((node) => node.id === "budget");
  if (budgetNode) {
    budgetNode.amount = totalIncome;
  }

  const remainingToAllocate = totalIncome - totalExpenses;
  if (remainingToAllocate !== 0) {
    const remainingGroupId = "group:remaining";
    const remainingLabel =
      remainingToAllocate >= 0 ? "Remaining" : "Over budget";
    orderedCategoryIds.push(remainingGroupId);
    upsertNode(
      remainingGroupId,
      remainingLabel,
      "remaining_group",
      Math.abs(remainingToAllocate),
    );
    upsertNode(
      "remaining",
      remainingLabel,
      "remaining",
      Math.abs(remainingToAllocate),
    );
    rawLinks.push({
      source: "budget",
      target: remainingGroupId,
      value: Math.abs(remainingToAllocate),
    });
    rawLinks.push({
      source: remainingGroupId,
      target: "remaining",
      value: Math.abs(remainingToAllocate),
    });
  }

  const incomeNodes = nodes.filter((node) => node.type === "income");
  const budgetNodes = nodes.filter((node) => node.type === "budget");
  const groupedNodes = orderedCategoryIds
    .map((id) => nodes.find((node) => node.id === id))
    .filter((node): node is BudgetPlanNode => Boolean(node));
  const leafNodes = nodes.filter(
    (node) =>
      !incomeNodes.some((incomeNode) => incomeNode.id === node.id) &&
      !budgetNodes.some((budgetNode) => budgetNode.id === node.id) &&
      !groupedNodes.some((groupNode) => groupNode.id === node.id),
  );
  const orderedNodes = [
    ...incomeNodes,
    ...budgetNodes,
    ...groupedNodes,
    ...leafNodes,
  ];

  const nodeIndexes = new Map(
    orderedNodes.map((node, index) => [node.id, index]),
  );
  const links = rawLinks
    .map((link) => {
      const source = nodeIndexes.get(link.source);
      const target = nodeIndexes.get(link.target);
      if (source === undefined || target === undefined || link.value <= 0) {
        return null;
      }

      return { source, target, value: link.value };
    })
    .filter((link): link is BudgetPlanLink => link !== null);

  if (totalIncome <= 0 || links.length === 0) {
    return {
      totalIncome,
      totalExpenses,
      totalInvestments,
      remainingToAllocate,
      nodes: [],
      links: [],
    };
  }

  return {
    totalIncome,
    totalExpenses,
    totalInvestments,
    remainingToAllocate,
    nodes: orderedNodes,
    links,
  };
}
